// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — RequestReturnModal
// ─────────────────────────────────────────────────────────────────────────────
// Step 1 of the return workflow (FEAT-SALE-STATUS-001): staff picks which
// items/quantities a customer is returning and why. Quantities are locked
// in here — condition/restock decisions happen later, at Complete, once
// the item is physically back (see CompleteReturnModal).
// ─────────────────────────────────────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Textarea } from '@/components/ui/FormControls';
import { useEffect, useState } from 'react';
import { X, RotateCcw, Loader2, Check } from 'lucide-react';
import { useRequestSaleReturn } from '@/hooks/useSales';
import { toast } from 'sonner';

export interface ReturnableItem {
  sale_item_id: string;
  product_name: string;
  sku?: string;
  variant_label?: string;
  quantity: number;
  returned_qty: number;
}

export interface RequestReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleId: string;
  saleNumber: string;
  items: ReturnableItem[];
  onSuccess?: () => void;
}

export default function RequestReturnModal({
  isOpen,
  onClose,
  saleId,
  saleNumber,
  items,
  onSuccess,
}: RequestReturnModalProps) {
  const requestReturn = useRequestSaleReturn();
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setQuantities({});
      setReason('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const returnable = items.filter((i) => i.quantity - i.returned_qty > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const selected = returnable
      .map((i) => ({ sale_item_id: i.sale_item_id, quantity: Number(quantities[i.sale_item_id] || 0), max: i.quantity - i.returned_qty }))
      .filter((i) => i.quantity > 0);

    if (selected.length === 0) {
      setError('Enter a quantity for at least one item');
      return;
    }
    const overLimit = selected.find((i) => i.quantity > i.max);
    if (overLimit) {
      setError('One or more quantities exceed what is left to return on that line');
      return;
    }
    if (!reason.trim()) {
      setError('A reason is required');
      return;
    }

    try {
      await requestReturn.mutateAsync({
        sale_id: saleId,
        items: selected.map(({ sale_item_id, quantity }) => ({ sale_item_id, quantity })),
        reason: reason.trim(),
      });
      toast.success('Return requested — awaiting approval');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to request return');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-return-title" data-field-guide-scope
    >
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-5 sm:p-6 shadow-2xl space-y-4 text-foreground">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <RotateCcw className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-start justify-between gap-3 pr-8">
                <h3 id="request-return-title" className="text-base font-bold text-foreground">
                  Request Return
                </h3>
                <UserGuide compact />
              </div>
              <p className="text-xs text-muted-foreground font-mono">Order {saleNumber}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={requestReturn.isPending}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50" aria-label="Close modal">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground">Items to Return</label>
            <div className="divide-y divide-border rounded-md border border-border">
              {returnable.length === 0 && (
                <p className="p-3 text-xs text-muted-foreground">Nothing left to return on this sale.</p>
              )}
              {returnable.map((item) => {
                const max = item.quantity - item.returned_qty;
                return (
                  <div key={item.sale_item_id} className="flex items-center justify-between gap-3 p-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{item.product_name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {[item.sku, item.variant_label].filter(Boolean).join(' · ')} — {max} available to return
                      </p>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={max}
                      disabled={requestReturn.isPending}
                      value={quantities[item.sale_item_id] ?? ''}
                      onChange={(e) => setQuantities((q) => ({ ...q, [item.sale_item_id]: e.target.value }))}
                      placeholder="0"
                      className="h-9 w-20 shrink-0 rounded-md border border-input bg-background px-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                      helpLabel="Items to Return"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="return-reason" className="text-xs font-semibold text-foreground">
              Reason <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="return-reason"
              rows={2}
              disabled={requestReturn.isPending}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Wrong size, customer changed mind, damaged on arrival..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 resize-none"
              helpLabel="Reason *"
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button type="button" onClick={onClose} disabled={requestReturn.isPending}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent text-foreground transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={requestReturn.isPending || returnable.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {requestReturn.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
